import {StopType} from "../../common/types";
import {
  EtaItemInput,
  Job,
  ManifestStop,
  NestedJobStop,
  RoutePlanningActivity,
  RoutePlanningDriverInput,
  RoutePlanningRouteEtas,
  RoutePlanningSolution,
  SubmitRoutePlanningInput
} from "../../../generated/graphql";
import {
  isActiveOrQueued,
  isDeliveryOrBothStop,
  isPickupStop,
  sortStopsByScheduledAndSequence
} from "../../../utils/stops/StopUtils";
import {RowData} from "../types/types";
import log from "loglevel";
import {ETASourceType} from "../../../constants/constants";
import {AssignmentDetailWithEndDateTime, RoutePlanningDriver} from "../../../providers/RoutePlanningProvider";
import {combineDatesAndTime} from "../../../utils/DayjsUtils";

/**
 * Determines the time window (leftbound and rightbound) for a stop.
 *
 * For Delivery (D) and Both (B) stops,
 * - The Leftbound follows this priority:
 *  1. Early time of the delivery/both stop, if provided
 *  2. The early time of the latest pickup stop on the job (by scheduledDateTime, Sequence)
 *  3. The scheduled time of the latest pickup stop on the job (by scheduledDateTime, Sequence)
 *  4. Epoch (fallback)
 * - Rightbound: Late time → Scheduled time → Null
 *
 * For Pickup (P) stops:
 * - Leftbound: Early time → Scheduled time → Epoch
 * - The Rightbound follows this priority:
 *  1. Late time
 *  2. If Leftbound is not equal to the Epoch, Leftbound + 4 hours
 *  3. Scheduled time of the first Delivery or Both stop (by scheduledDateTime, Sequence)
 *  4. Late time of the first Delivery or Both stop (by scheduledDateTime, Sequence)
 *  5. Null
 *
 */
export const getStopTimeWindow = (stop: NestedJobStop, job: Job): [Date | null, Date | null] => {
  const epoch = new Date(0);
  const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

  let leftbound: Date | null = null;
  let rightbound: Date | null = null;

  if (stop.stopType === StopType.PICKUP) {
    if (stop.earlyDateTime) {
      leftbound = new Date(stop.earlyDateTime);
    } else if (stop.scheduledDateTime) {
      leftbound = new Date(stop.scheduledDateTime);
    } else {
      leftbound = epoch;
    }

    if (stop.lateDateTime) {
      rightbound = new Date(stop.lateDateTime);
    } else if (leftbound.getTime() > 0) {
      rightbound = new Date(leftbound.getTime() + FOUR_HOURS_MS);
    } else {
      const deliveryStops = job.stops.filter((s) =>
        [StopType.DELIVERY, StopType.BOTH].includes(s.stopType as StopType)
      );
      if (deliveryStops.length > 0) {
        const firstDelivery = sortStopsByScheduledAndSequence(deliveryStops)[0];
        if (firstDelivery.scheduledDateTime) {
          rightbound = new Date(firstDelivery.scheduledDateTime);
        } else if (firstDelivery.lateDateTime) {
          rightbound = new Date(firstDelivery.lateDateTime);
        }
      }
    }
  } else {
    if (stop.earlyDateTime) {
      leftbound = new Date(stop.earlyDateTime);
    } else {
      const pickupStops = job.stops.filter((s) => s.stopType === StopType.PICKUP);
      if (pickupStops.length > 0) {
        const sortedPickup = sortStopsByScheduledAndSequence(pickupStops, "desc");
        const latestPickup = sortedPickup[0];
        if (latestPickup.earlyDateTime) {
          leftbound = new Date(latestPickup.earlyDateTime);
        } else if (latestPickup.scheduledDateTime) {
          leftbound = new Date(latestPickup.scheduledDateTime);
        } else {
          leftbound = epoch;
        }
      } else {
        leftbound = epoch;
      }
    }

    if (stop.lateDateTime) {
      rightbound = new Date(stop.lateDateTime);
    } else if (stop.scheduledDateTime) {
      rightbound = new Date(stop.scheduledDateTime);
    }
  }

  return [leftbound, rightbound];
};

/** Build a unique GH vehicle_id from a driver-like object. */
export const toVehicleId = (driver: {driverId?: number | null; manifestDriverId?: number | null}): string =>
  driver.manifestDriverId ? `manifest:${driver.manifestDriverId}` : `driver:${driver.driverId}`;

/**
 * Resolves a GH vehicle_id ("driver:{driverId}" | "manifest:{manifestDriverId}")
 * to a driver from a list. Works with any driver-like type that has driverId
 * and manifestDriverId fields.
 */
export const findDriverByVehicleId = <T extends {driverId?: number | null; manifestDriverId?: number | null}>(
  vehicleId: string | null | undefined,
  drivers: T[]
): T | undefined => {
  if (!vehicleId) return undefined;
  const [prefix, idStr] = vehicleId.split(":");
  if (prefix === "manifest" && idStr) {
    return drivers.find((d) => d.manifestDriverId === Number(idStr));
  }
  if (prefix === "driver" && idStr) {
    return drivers.find((d) => !d.manifestDriverId && d.driverId === Number(idStr));
  }
  return undefined;
};

export const generateGraphhopperAssignmentDetails = (
  solution: RoutePlanningSolution,
  startDate: string,
  dismissJobIds: number[],
  submittedInput: SubmitRoutePlanningInput,
  isEtaFromOptimizationEnabled: boolean,
  routeEtas: RoutePlanningRouteEtas[] | null = null
): AssignmentDetailWithEndDateTime[] => {
  const assignmentDetails: AssignmentDetailWithEndDateTime[] = [];
  const submittedDrivers = (submittedInput?.drivers ?? []).filter((d): d is NonNullable<typeof d> => d != null);
  const routeEtasByVehicleId = new Map((routeEtas ?? []).map((routeEta) => [routeEta.vehicleId, routeEta]));
  solution.routes?.forEach((route) => {
    const driver = findDriverByVehicleId(route.vehicle_id, submittedDrivers);
    if (!driver || !route.activities) {
      log.warn("Driver not found for route", route);
      return;
    }

    const stopActivities = getStopActivities(route.activities);
    const manifestDriverId = driver.manifestDriverId;

    // DP-3175: Existing manifests always appear on the map, even if empty
    if (stopActivities.length === 0 && !manifestDriverId) {
      return;
    }

    const jobIds = new Set<number>();
    // DP-3228: a Set, not an array. getUniqueActivities de-duplicates on
    // `location_id`+`type`, so a stop that is BOTH the pickup and the delivery of a
    // shipment survives twice — which happens for every pickup-only / delivery-only
    // job (self-paired shipments) and for every Both stop (delivery of one shipment,
    // pickup of the next). Pushing the jobStopId twice then breaks AG Grid's getRowId,
    // double-counts stops in Route Cost, shifts manifestSequence, and makes the
    // route-signature baseline in RoutesResultMap never match — the DP-3100 bug.
    // This was a Set until edc219a95 (DP-3175) changed it, correctly at the time:
    // under the Service model one stop yields one activity and duplicates cannot
    // occur, so restoring the Set is a no-op there and a fix on the shipment path.
    // Insertion order is preserved, which is what the sequence semantics rely on.
    const sequences = new Set<number>();
    let isRouteChanged = false;

    // Add stops from route activities (backend provides all stops including
    // completed ones for untouched/rebuilt manifest routes)
    stopActivities.forEach((activity) => {
      const [jobId, jobStopId] = extractJobAndJobStopIdsFromActivity(activity);
      if (jobId && dismissJobIds?.includes(jobId)) {
        isRouteChanged = true;
        return;
      }
      if (jobId && jobStopId) {
        jobIds.add(jobId);
        sequences.add(jobStopId);
      }
    });

    if (jobIds.size === 0 && !manifestDriverId) {
      return;
    }

    // Server-computed optimization ETAs (DP-3121): the route-planning lambda
    // owns the dwell walk, the wait-for-early-time-window clamp, and the
    // start-time fallback; the frontend only consumes the returned values.
    // Routes with dismissed jobs no longer match the solved sequence, so their
    // server ETAs are discarded (requestEtaIfEmpty covers them at create time).
    const routeEta = !isRouteChanged ? routeEtasByVehicleId.get(route.vehicle_id ?? "") : undefined;

    // The server start is deliberately NOT gated on isEtaFromOptimizationEnabled:
    // the start-time derivation (submitted start, else scheduled minus driving
    // time, never in the past — clamping now happens server-side at poll time)
    // was never flag-gated before it moved to the lambda, and the server value
    // is the only remaining source of the scheduled-time fallback. Clamping it
    // again here would desync the start from the ETAs computed off it.
    let driverStartDateTime: Date;
    const now = new Date();
    if (routeEta?.startDateTime) {
      driverStartDateTime = new Date(routeEta.startDateTime);
    } else {
      const submittedStart = driver.startTime ? new Date(driver.startTime) : null;
      driverStartDateTime = submittedStart && submittedStart >= now ? submittedStart : now;
    }

    const useServerEtas = isEtaFromOptimizationEnabled && !isRouteChanged && routeEta != null;
    const fallbackGeneratedDateTime = new Date().toISOString();
    // DP-3228: de-duplicate by jobStopId for the same reason `sequences` is a Set —
    // computeRouteEtas emits one ETA per ACTIVITY, and under the shipment model a
    // self-paired or Both stop is two activities at one location. Keep the first: it
    // is the earlier leg in route order, i.e. the arrival. Without this,
    // optimizationEtas and sequences disagree in length, and RoutePlanningView
    // submits optimizationEtas verbatim to processRoutePlanning whenever the plan is
    // unmodified — so the duplicate would be written, not just displayed. (It is
    // invisible in manual QA of a modified plan, because rebuildAssignmentDetails
    // re-derives the ETAs from assignedStops.)
    const seenEtaStopIds = new Set<number>();
    const stopEtas: EtaItemInput[] = useServerEtas
      ? (routeEta.etas ?? [])
          .filter(
            (item): item is {jobStopId: number; eta: string; etaSource?: string; generatedDateTime?: string} =>
              item?.jobStopId != null && !!item.eta
          )
          .filter((item) => {
            if (seenEtaStopIds.has(item.jobStopId)) return false;
            seenEtaStopIds.add(item.jobStopId);
            return true;
          })
          .map((item) => ({
            jobStopId: item.jobStopId,
            eta: item.eta,
            etaSource: item.etaSource ?? ETASourceType.ROUTE_PLANNING,
            generatedDateTime: item.generatedDateTime ?? fallbackGeneratedDateTime
          }))
      : [];
    // DP-3175 B04: endDateTime is always read from the server when available,
    // regardless of the etaFromOptimization flag. The flag gates per-stop ETAs
    // (shown in the detail grid), but the route-level endDateTime is needed for
    // the master grid's End Time column and Route Cost duration in all cases.
    // routeEta is already undefined for changed routes, so no extra guard needed.
    const endTime = routeEta?.endDateTime ?? undefined;

    assignmentDetails.push({
      submittedDriver: {...driver, startTime: driverStartDateTime.toISOString()},
      dwellTimeSeconds: submittedInput.dwellTimeSeconds ?? 0,
      jobIds: Array.from(jobIds),
      sequences: Array.from(sequences),
      startDate: startDate,
      optimizationEtas: stopEtas,
      endDateTime: endTime,
      // DP-3175 B05: always request backend ETA computation when the frontend
      // didn't supply per-stop ETAs. The etaFromOptimization flag gates whether
      // the frontend USES server ETAs in the UI, not whether the backend should
      // compute them at manifest-creation time.
      requestEtaIfEmpty: true
    });
  });

  return assignmentDetails;
};

const isStopActivity = (activity: RoutePlanningActivity): boolean => {
  return activity.type !== "start" && activity.type !== "end";
};

const getUniqueActivities = (activities: RoutePlanningActivity[]): RoutePlanningActivity[] => {
  const uniqueMap = new Map<string, RoutePlanningActivity>();
  activities.forEach((act) => {
    const key = `${act.location_id}_${act.type}`;
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, act);
    }
  });
  return Array.from(uniqueMap.values());
};

export const getStopActivities = (activities: RoutePlanningActivity[] | null | undefined): RoutePlanningActivity[] => {
  if (!activities) return [];
  return getUniqueActivities(activities.filter(isStopActivity));
};

export const extractJobAndJobStopIdsFromActivity = (
  activity: RoutePlanningActivity | null
): [number | null, number | null] => {
  if (!activity?.location_id) {
    return [null, null];
  }
  const parts = activity.location_id.split(":");
  // New job stops: "job:jobId:jobStopId"
  if (parts.length === 3 && parts[0] === "job") return [Number(parts[1]), Number(parts[2])];
  // Manifest stops: "manifest:jobId:jobStopId"
  if (parts.length === 3 && parts[0] === "manifest") return [Number(parts[1]), Number(parts[2])];
  // Legacy format: "jobId:jobStopId" (no prefix)
  if (parts.length === 2) return [Number(parts[0]), Number(parts[1])];
  return [null, null];
};

/**
 * Extracts unique jobIds from unassigned details that belong to manifest stops.
 * Manifest stops have service IDs in the format "manifest:{jobId}:{jobStopId}".
 */
export const getUnassignedManifestJobIds = (
  unassignedDetails: Array<{id?: string | null; code?: number | null; reason?: string | null}>
): number[] => {
  const jobIds = new Set<number>();
  for (const detail of unassignedDetails) {
    if (detail.id?.startsWith("manifest:")) {
      const jobId = Number(detail.id.split(":")[1]);
      if (Number.isFinite(jobId)) {
        jobIds.add(jobId);
      }
    }
  }
  return Array.from(jobIds);
};

export const rebuildAssignmentDetails = (
  workingRowData: RowData[],
  originalAssignmentDetails: AssignmentDetailWithEndDateTime[],
  submittedDwellSeconds: number
): AssignmentDetailWithEndDateTime[] => {
  const fallbackStartDate = originalAssignmentDetails[0]?.startDate ?? "";

  return workingRowData.map((row) => {
    // Match by manifestDriverId when present to disambiguate same-driver manifest rows.
    const original = originalAssignmentDetails.find((a) =>
      row.manifestDriverId
        ? a.submittedDriver.manifestDriverId === row.manifestDriverId
        : a.submittedDriver.driverId === row.driverId
    );
    const jobIds = [...new Set(row.assignedStops.map((s) => s.job?.jobId).filter(Boolean))] as number[];
    const sequences = row.assignedStops.map((s) => s.jobStopId);

    const optimizationEtas: EtaItemInput[] = row.assignedStops
      .filter((s) => s.eta)
      .map((s) => ({
        jobStopId: s.jobStopId,
        eta: s.eta as string,
        etaSource: s.etaSource ?? ETASourceType.ROUTE_PLANNING,
        generatedDateTime: s.generatedDateTime ?? new Date().toISOString()
      }));

    // DP-3175 B11: When a row has no manifestDriverId (new route), the matched
    // original may belong to an existing manifest with the same driverId (after
    // Change Driver). Inheriting that original's submittedDriver would carry
    // manifestDriverId, causing the backend to merge the new route's stops into
    // the existing manifest — producing duplicate sequences and blank ETAs.
    // Build a fresh submittedDriver from the row to keep it as a new manifest.
    const baseDriver = original?.submittedDriver ?? null;
    const submittedDriver: RoutePlanningDriverInput =
      row.manifestDriverId && baseDriver
        ? baseDriver
        : {
            driverId: row.driverId!,
            startTime: baseDriver?.startTime ?? row.startTime?.toISOString?.() ?? null,
            startAddress: baseDriver?.startAddress ?? row.startLocation ?? null,
            startAddressPosition:
              baseDriver?.startAddressPosition ??
              (row.startLat != null && row.startLng != null
                ? {latitude: row.startLat, longitude: row.startLng}
                : undefined),
            endAddress: baseDriver?.endAddress ?? row.endLocation ?? null,
            endAddressPosition: baseDriver?.endAddressPosition,
            skillsIds: baseDriver?.skillsIds
          };

    return {
      submittedDriver,
      dwellTimeSeconds: original?.dwellTimeSeconds ?? submittedDwellSeconds,
      jobIds,
      sequences,
      startDate: original?.startDate ?? fallbackStartDate,
      optimizationEtas,
      requestEtaIfEmpty: true
    };
  });
};

const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

/**
 * DP-3202: Calculate the effective late time for a manifest stop.
 *
 * - If `lateDateTime` exists, return it directly.
 * - If `scheduledDateTime` exists and stopType is "P" (pickup), return scheduled + 4 hours.
 * - If `scheduledDateTime` exists and stopType is "D" or "B", return scheduled time.
 * - Otherwise return null (no constraint).
 */
export const calculateLateTime = (stop: ManifestStop): Date | null => {
  if (stop.lateDateTime) {
    return new Date(stop.lateDateTime);
  }
  if (stop.scheduledDateTime) {
    if (isPickupStop(stop)) {
      return new Date(new Date(stop.scheduledDateTime).getTime() + FOUR_HOURS_MS);
    }
    if (isDeliveryOrBothStop(stop)) {
      return new Date(stop.scheduledDateTime);
    }
  }
  return null;
};

/**
 * DP-3202: Identify manifests that contain at least one active stop already past
 * its late time — or whose late time falls before every driver's start time.
 *
 * When `allowMovingExistingWork` is ON, a manifest stop is flagged if no
 * selected driver can serve it in time. When OFF, only the manifest's own
 * driver is checked.
 */
export const getManifestsWithLateStops = (
  manifestStopsMap: Record<number, ManifestStop[]>,
  selectedDrivers: RoutePlanningDriver[],
  allowMovingExistingWork: boolean
): Array<{manifestDriverId: number; driverName: string}> => {
  const now = new Date();
  const result: Array<{manifestDriverId: number; driverName: string}> = [];

  for (const [manifestDriverIdStr, stops] of Object.entries(manifestStopsMap)) {
    const manifestDriverId = Number(manifestDriverIdStr);
    const manifestDriver = selectedDrivers.find((d) => d.manifestDriverId === manifestDriverId);
    if (!manifestDriver) continue;

    let isFlagged = false;

    for (const stop of stops) {
      // Only check active stops (jobStopStatus "A" or "Q")
      if (!isActiveOrQueued(stop)) continue;

      const lateTime = calculateLateTime(stop);
      if (!lateTime) continue;

      // Already past late time
      if (lateTime < now) {
        isFlagged = true;
        break;
      }

      if (allowMovingExistingWork) {
        // Check if ANY driver can serve this stop in time
        const canAnyDriverServe = selectedDrivers.some((driver) => {
          const driverStart = combineDatesAndTime(driver.startDate, driver.startTime, driver.startTimeZone);
          return driverStart.isValid() && driverStart.toDate() <= lateTime;
        });
        if (!canAnyDriverServe) {
          isFlagged = true;
          break;
        }
      } else {
        // Only check the manifest's own driver
        const ownDriverStart = combineDatesAndTime(
          manifestDriver.startDate,
          manifestDriver.startTime,
          manifestDriver.startTimeZone
        );
        if (ownDriverStart.isValid() && ownDriverStart.toDate() > lateTime) {
          isFlagged = true;
          break;
        }
      }
    }

    if (isFlagged) {
      result.push({manifestDriverId, driverName: manifestDriver.name ?? `Driver ${manifestDriver.driverCode}`});
    }
  }

  return result;
};
